import { useEffect, useMemo, useState, type ReactNode } from "react";
import Modal from "../../components/Modal";
import Toggle from "../../components/Toggle";
import { Banner } from "../../components/Feedback";
import type { Question, Suggestion } from "./quizTypes";
import {
  MIN_ANSWERS,
  allForms,
  canonicalOf,
  formsOf,
  normalize,
  normalizeAnswerLine,
  relativeTime,
} from "./quizText";

type Props = {
  question: Question;
  /** Bu soruya gelen, henüz karara bağlanmamış oyuncu cevapları. */
  suggestions: Suggestion[];
  busy: boolean;
  onSave: (patch: Partial<Question>) => Promise<void>;
  onDelete: () => void;
  onAcceptSuggestion: (s: Suggestion) => Promise<void>;
  onDismissSuggestion: (s: Suggestion) => Promise<void>;
  onClose: () => void;
};

/**
 * Tek bir sorunun düzenleme ekranı.
 *
 * Sayfadan ayrı bir bileşen çünkü listeyle hiçbir durumu paylaşmıyor:
 * burada tutulan her şey (taslak metin, açık sekme, cevap araması) modal
 * kapanınca çöpe gidiyor. Aynı dosyada dursaydı liste her tuş vuruşunda
 * yeniden çizilirdi.
 */
export default function QuizDetail({
  question,
  suggestions,
  busy,
  onSave,
  onDelete,
  onAcceptSuggestion,
  onDismissSuggestion,
  onClose,
}: Props) {
  const [tab, setTab] = useState<"answers" | "rejected">("answers");
  const [text, setText] = useState(question.text);
  const [tagInput, setTagInput] = useState("");
  const [bulk, setBulk] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<{ line: string; draft: string } | null>(
    null
  );
  const [report, setReport] = useState<string[] | null>(null);

  // Başka bir soruya geçilince taslak metin eskisinde kalmasın.
  useEffect(() => {
    setText(question.text);
    setEditing(null);
    setReport(null);
    setSearch("");
    setBulk("");
    setTab("answers");
  }, [question.id]);

  const playable = question.answers.length >= MIN_ANSWERS;
  const missing = Math.max(0, MIN_ANSWERS - question.answers.length);

  const shownAnswers = useMemo(() => {
    const q = normalize(search);
    if (!q) return question.answers;
    return question.answers.filter((line) => line.includes(q));
  }, [question.answers, search]);

  const commitText = async () => {
    const next = text.trim();
    if (!next || next === question.text) {
      setText(question.text);
      return;
    }
    await onSave({ text: next });
  };

  /** Mükerrer kontrolü tüm yazımlara karşı — eşanlamlı da çakışmamalı. */
  const addAnswers = async () => {
    const incoming = bulk
      .split("\n")
      .map(normalizeAnswerLine)
      .filter(Boolean);
    if (!incoming.length) return;

    const known = allForms(question.answers);
    const fresh: string[] = [];
    const dupes: string[] = [];
    for (const line of incoming) {
      const forms = formsOf(line);
      if (forms.some((f) => known.has(f))) {
        dupes.push(canonicalOf(line));
        continue;
      }
      forms.forEach((f) => known.add(f));
      fresh.push(line);
    }

    const lines: string[] = [];
    if (fresh.length) lines.push(`${fresh.length} cevap eklendi.`);
    if (dupes.length) {
      lines.push(
        `${dupes.length} tanesi zaten vardı: ${dupes.slice(0, 8).join(", ")}${
          dupes.length > 8 ? "…" : ""
        }`
      );
    }
    setReport(lines);
    if (!fresh.length) return;

    await onSave({ answers: [...question.answers, ...fresh] });
    setBulk("");
  };

  const removeAnswer = (line: string) =>
    onSave({ answers: question.answers.filter((a) => a !== line) });

  const commitEdit = async () => {
    if (!editing) return;
    const next = normalizeAnswerLine(editing.draft);
    const original = editing.line;
    setEditing(null);
    if (!next || next === original) return;

    // Düzenlenen satır dışındaki yazımlarla çakışıyorsa yazma — sessizce
    // iki aynı cevap oluşmasın.
    const others = question.answers.filter((a) => a !== original);
    const known = allForms(others);
    if (formsOf(next).some((f) => known.has(f))) {
      setReport([`"${canonicalOf(next)}" başka bir cevapta zaten var.`]);
      return;
    }
    await onSave({
      answers: question.answers.map((a) => (a === original ? next : a)),
    });
  };

  const addTag = async () => {
    const t = normalize(tagInput);
    if (!t || question.tags.includes(t)) {
      setTagInput("");
      return;
    }
    setTagInput("");
    await onSave({ tags: [...question.tags, t] });
  };

  const removeTag = (t: string) =>
    onSave({ tags: question.tags.filter((x) => x !== t) });

  return (
    <Modal
      open
      wide
      title="Soruyu düzenle"
      subtitle={`${question.answers.length} cevap · ${
        question.enabled ? "yayında" : "taslak"
      } · son güncelleme ${relativeTime(question.updatedAtMs)}`}
      onClose={onClose}
      footer={
        <>
          <button
            className="btn-ghost text-sm text-danger mr-auto"
            disabled={busy}
            onClick={onDelete}
          >
            Soruyu sil
          </button>
          <button className="btn-outline text-sm" onClick={onClose}>
            Kapat
          </button>
        </>
      }
    >
      <label className="section-label block mb-2">Soru metni</label>
      <input
        className="input text-base font-bold mb-4"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commitText}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />

      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Toggle
            checked={question.enabled}
            disabled={busy || (!playable && !question.enabled)}
            onChange={(v) => onSave({ enabled: v })}
            title={
              playable
                ? "Oyunda göster / gizle"
                : `Yayına almak için ${missing} cevap daha gerekiyor`
            }
          />
          <span className="text-sm font-semibold">
            {question.enabled ? "Yayında" : "Taslak"}
          </span>
        </div>

        <span
          className={`pill ${
            playable
              ? "bg-success/15 text-success"
              : "bg-warning/15 text-warning"
          }`}
        >
          {question.answers.length} / {MIN_ANSWERS} cevap
        </span>

        {!playable && (
          <span className="text-xs text-warning">
            {missing} cevap daha girilmeden oyuna giremez.
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-5">
        {question.tags.map((t) => (
          <span
            key={t}
            className="pill bg-surfaceHigh text-textSecondary gap-1"
          >
            {t}
            <button
              className="text-textMuted hover:text-danger"
              disabled={busy}
              onClick={() => removeTag(t)}
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="bg-transparent border border-dashed border-stroke rounded-full px-3 py-1 text-xs w-36 focus:outline-none focus:border-primary"
          placeholder="+ etiket"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onBlur={addTag}
          onKeyDown={(e) => {
            if (e.key === "Enter") void addTag();
          }}
        />
      </div>

      <div className="flex items-center gap-1 border-b border-stroke mb-4">
        <Tab active={tab === "answers"} onClick={() => setTab("answers")}>
          Cevaplar
          <Count>{question.answers.length}</Count>
        </Tab>
        <Tab active={tab === "rejected"} onClick={() => setTab("rejected")}>
          Reddedilenler
          {suggestions.length > 0 && (
            <Count tone="warn">{suggestions.length}</Count>
          )}
        </Tab>
      </div>

      {report && (
        <Banner tone="info" onClose={() => setReport(null)}>
          <ul className="space-y-0.5">
            {report.map((l, i) => (
              <li key={i}>• {l}</li>
            ))}
          </ul>
        </Banner>
      )}

      {tab === "answers" ? (
        <>
          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="section-label block mb-2">Cevap ekle</label>
              <textarea
                className="input h-28 text-sm font-mono"
                placeholder={
                  "Her satıra bir cevap.\nEşanlamlıları | ile ayır:\n\namerika birleşik devletleri|abd|amerika"
                }
                value={bulk}
                onChange={(e) => setBulk(e.target.value)}
              />
              <div className="flex items-center gap-3 mt-2">
                <button
                  className="btn-primary py-2 px-4 text-sm"
                  disabled={busy || !bulk.trim()}
                  onClick={addAnswers}
                >
                  {busy ? "…" : "Ekle"}
                </button>
                <span className="text-[11px] text-textMuted">
                  Mükerrerler eşanlamlılar dahil kontrol edilir.
                </span>
              </div>
            </div>
            <div>
              <label className="section-label block mb-2">
                Listede ara
              </label>
              <input
                className="input py-2 text-sm"
                placeholder="Cevap ara…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <p className="text-[11px] text-textMuted mt-2 leading-relaxed">
                Bir cevaba tıklayınca satırı düzenlersin. Eşanlamlıları
                <code className="mx-1">|</code>ile ekle; ilk yazım kanonik
                olandır, oyunda o görünür.
              </p>
            </div>
          </div>

          {question.answers.length === 0 ? (
            <p className="text-sm text-textMuted py-6 text-center">
              Henüz cevap yok. Yukarıdan ekle.
            </p>
          ) : (
            <div className="max-h-[38vh] overflow-y-auto -mx-1 px-1">
              <div className="flex flex-wrap gap-1.5">
                {shownAnswers.map((line) => {
                  const forms = formsOf(line);
                  const isEditing = editing?.line === line;
                  if (isEditing) {
                    return (
                      <input
                        key={line}
                        autoFocus
                        className="input py-1.5 px-3 text-[13px] font-mono w-72"
                        value={editing.draft}
                        onChange={(e) =>
                          setEditing({ line, draft: e.target.value })
                        }
                        onBlur={commitEdit}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void commitEdit();
                          if (e.key === "Escape") setEditing(null);
                        }}
                      />
                    );
                  }
                  return (
                    <span
                      key={line}
                      className="group inline-flex items-center gap-1.5 text-[13px] bg-surfaceHigh hover:bg-surfaceOverlay rounded-lg pl-3 pr-1.5 py-1.5 transition"
                      title={
                        forms.length > 1
                          ? `Eşanlamlılar: ${forms.slice(1).join(", ")}`
                          : "Düzenlemek için tıkla"
                      }
                    >
                      <button
                        className="text-left"
                        onClick={() => setEditing({ line, draft: line })}
                      >
                        {forms[0]}
                      </button>
                      {forms.length > 1 && (
                        <span className="text-[10px] text-primarySoft font-bold">
                          +{forms.length - 1}
                        </span>
                      )}
                      <button
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-textMuted hover:text-danger px-1 rounded transition"
                        disabled={busy}
                        onClick={() => removeAnswer(line)}
                        title="Sil"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
              {search && shownAnswers.length === 0 && (
                <p className="text-sm text-textMuted py-4 text-center">
                  Eşleşen cevap yok.
                </p>
              )}
            </div>
          )}
        </>
      ) : (
        <RejectedTab
          suggestions={suggestions}
          busy={busy}
          onAccept={onAcceptSuggestion}
          onDismiss={onDismissSuggestion}
        />
      )}
    </Modal>
  );
}

/**
 * Bu soruya gelen oyuncu önerileri.
 *
 * Kaynak, oyunun reddettiği cevaplar: oyuncu ciddi bir kelime yazmış ama
 * listede yok. Havuzu tahminle doldurmaktan çok daha isabetli bir sinyal,
 * o yüzden sorunun kendi ekranında duruyor — düzelteceğin yerde.
 */
function RejectedTab({
  suggestions,
  busy,
  onAccept,
  onDismiss,
}: {
  suggestions: Suggestion[];
  busy: boolean;
  onAccept: (s: Suggestion) => Promise<void>;
  onDismiss: (s: Suggestion) => Promise<void>;
}) {
  if (suggestions.length === 0) {
    return (
      <div className="text-center py-10">
        <div className="text-3xl mb-2">✅</div>
        <p className="text-sm text-textSecondary">
          Bu soruda bekleyen öneri yok.
        </p>
        <p className="text-[11px] text-textMuted mt-1">
          Oyuncular listede olmayan bir cevap yazdığında burada birikir.
        </p>
      </div>
    );
  }

  return (
    <div className="max-h-[42vh] overflow-y-auto -mx-1 px-1">
      <div className="divide-y divide-stroke/70">
        {suggestions.map((s) => (
          <div key={s.id} className="flex items-center gap-3 py-2.5">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm">{s.word}</div>
              <div className="text-[11px] text-textMuted">
                {relativeTime(s.lastAtMs)}
              </div>
            </div>
            <span
              className={`pill tabular-nums ${
                s.count >= 3
                  ? "bg-warning/20 text-warning"
                  : "bg-surfaceHigh text-textMuted"
              }`}
            >
              {s.count} deneme
            </span>
            <button
              className="btn-primary py-1.5 px-3 text-xs"
              disabled={busy}
              onClick={() => onAccept(s)}
            >
              Cevaba ekle
            </button>
            <button
              className="btn-ghost py-1.5 px-3 text-xs"
              disabled={busy}
              onClick={() => onDismiss(s)}
              title="Geçerli bir cevap değil — kuyruktan kaldır"
            >
              Yok say
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-4 py-2 text-sm font-bold border-b-2 -mb-px transition flex items-center gap-2",
        active
          ? "border-primary text-textPrimary"
          : "border-transparent text-textMuted hover:text-textSecondary",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function Count({
  children,
  tone,
}: {
  children: ReactNode;
  tone?: "warn";
}) {
  return (
    <span
      className={`pill text-[10px] px-1.5 py-0.5 ${
        tone === "warn"
          ? "bg-warning/20 text-warning"
          : "bg-surfaceHigh text-textMuted"
      }`}
    >
      {children}
    </span>
  );
}
